// 物件カード：ボタン以外のタップで詳細へ（全面リンクの補助）
// SPでスタッキングの都合によりリンクが効かない場合の保険にもなる

const INTERACTIVE_SELECTOR = [
    'a',
    'button',
    'input',
    'label',
    'textarea',
    'select',
    '.c-buttons-favorite',
    '.js-property-gallery-open',
].join(',');

const parseGalleryImages = (card) => {
    try {
        const images = JSON.parse(card.dataset.galleryImages || '[]');
        return Array.isArray(images)
            ? images.filter((image) => image && (typeof image === 'string' || image.src))
            : [];
    } catch (error) {
        console.warn('[cards-estate] invalid gallery images', error);
        return [];
    }
};

const imageSrc = (image) => (typeof image === 'string' ? image : image.src);
const imageAlt = (image) => (typeof image === 'string' ? '' : (image.alt || ''));
const GALLERY_FADE_FALLBACK_MS = 900;
const GALLERY_SWIPE_THRESHOLD_PX = 40;
const GALLERY_DRAG_THRESHOLD_PX = 10;
const GALLERY_CLICK_SUPPRESS_MS = 700;
const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const supportsPointerEvents = () => 'PointerEvent' in window;

const bindGalleryNav = (card) => {
    if (card.dataset.cardsEstateGalleryBound) return;

    const images = parseGalleryImages(card);
    if (images.length <= 1) return;

    const img = card.querySelector('.c-cards-estate__gallery img');
    const gallery = card.querySelector('.c-cards-estate__gallery');
    const prev = card.querySelector('.js-estate-card-gallery-prev');
    const next = card.querySelector('.js-estate-card-gallery-next');
    if (!gallery || !img || !prev || !next) return;

    card.dataset.cardsEstateGalleryBound = '1';
    let transitionId = 0;
    let queuedDirection = 0;
    let pointerStart = null;
    let touchStart = null;
    let suppressNextGalleryClick = false;
    let suppressClickTimer = null;

    const beginSwipe = (id, x, y) => ({
        id,
        x,
        y,
        lastX: x,
        lastY: y,
        swiping: false,
    });

    const updateSwipe = (state, x, y) => {
        if (!state) return null;

        const dx = x - state.x;
        const dy = y - state.y;
        state.lastX = x;
        state.lastY = y;
        if (Math.abs(dx) > GALLERY_DRAG_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
            state.swiping = true;
        }

        return { dx, dy };
    };

    const suppressGalleryClick = () => {
        suppressNextGalleryClick = true;
        window.clearTimeout(suppressClickTimer);
        suppressClickTimer = window.setTimeout(() => {
            suppressNextGalleryClick = false;
        }, GALLERY_CLICK_SUPPRESS_MS);
    };

    const finishSwipe = (state, x, y) => {
        if (!state) return { handled: false, changed: false };

        const dx = x - state.x;
        const dy = y - state.y;
        const wasHorizontalDrag = state.swiping
            && Math.abs(dx) > GALLERY_DRAG_THRESHOLD_PX
            && Math.abs(dx) > Math.abs(dy);
        if (!wasHorizontalDrag) return { handled: false, changed: false };

        suppressGalleryClick();

        const shouldChangeImage = Math.abs(dx) >= GALLERY_SWIPE_THRESHOLD_PX
            && Math.abs(dx) > Math.abs(dy) * 1.2;

        if (!shouldChangeImage) return { handled: true, changed: false };

        show(dx < 0 ? 1 : -1);
        return { handled: true, changed: true };
    };

    const show = (direction) => {
        if (card.dataset.cardsEstateGalleryTransitioning) {
            queuedDirection = direction;
            return;
        }

        const current = Number.parseInt(img.dataset.galleryIndex || '0', 10);
        const nextIndex = (current + direction + images.length) % images.length;
        const nextImage = images[nextIndex];
        const src = imageSrc(nextImage);
        if (!src) return;

        card.dataset.cardsEstateGalleryTransitioning = '1';
        transitionId += 1;
        const ownTransitionId = transitionId;
        const alt = imageAlt(nextImage);
        const preloader = new Image();

        const runQueuedTransition = () => {
            const directionToRun = queuedDirection;
            queuedDirection = 0;
            if (directionToRun) {
                window.setTimeout(() => show(directionToRun), 0);
            }
        };

        const finishWithoutAnimation = () => {
            img.src = src;
            img.alt = alt;
            img.dataset.galleryIndex = String(nextIndex);
            delete card.dataset.cardsEstateGalleryTransitioning;
            runQueuedTransition();
        };

        preloader.onload = () => {
            if (ownTransitionId !== transitionId) return;

            const overlay = document.createElement('img');
            let finished = false;
            overlay.className = 'c-cards-estate__gallery-fade';
            overlay.src = src;
            overlay.alt = alt;
            overlay.decoding = 'async';

            const finish = () => {
                if (finished) return;
                finished = true;
                img.src = src;
                img.alt = alt;
                img.dataset.galleryIndex = String(nextIndex);
                overlay.remove();
                delete card.dataset.cardsEstateGalleryTransitioning;
                runQueuedTransition();
            };

            overlay.addEventListener('transitionend', finish, { once: true });
            gallery.appendChild(overlay);
            if (prefersReducedMotion()) {
                finish();
                return;
            }
            requestAnimationFrame(() => overlay.classList.add('is-visible'));
            window.setTimeout(finish, GALLERY_FADE_FALLBACK_MS);
        };

        preloader.onerror = finishWithoutAnimation;
        preloader.src = src;
    };

    prev.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        show(-1);
    });

    next.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        show(1);
    });

    gallery.addEventListener('dragstart', (e) => {
        e.preventDefault();
    });

    if (supportsPointerEvents()) {
        gallery.addEventListener('pointerdown', (e) => {
            if (e.button !== undefined && e.button !== 0) return;
            pointerStart = beginSwipe(e.pointerId, e.clientX, e.clientY);
            gallery.setPointerCapture?.(e.pointerId);
        });

        gallery.addEventListener('pointermove', (e) => {
            if (!pointerStart || e.pointerId !== pointerStart.id) return;

            const delta = updateSwipe(pointerStart, e.clientX, e.clientY);
            if (pointerStart.swiping && delta && Math.abs(delta.dx) > Math.abs(delta.dy)) {
                e.preventDefault();
            }
        });

        const endSwipe = (e) => {
            if (!pointerStart || e.pointerId !== pointerStart.id) return;

            const gesture = finishSwipe(pointerStart, e.clientX, e.clientY);
            pointerStart = null;
            gallery.releasePointerCapture?.(e.pointerId);

            if (!gesture.handled) return;

            e.preventDefault();
            e.stopPropagation();
        };

        gallery.addEventListener('pointerup', endSwipe);
        gallery.addEventListener('pointercancel', (e) => {
            pointerStart = null;
            gallery.releasePointerCapture?.(e.pointerId);
        });
    } else {
        gallery.addEventListener('touchstart', (e) => {
            if (e.touches.length !== 1) return;
            const touch = e.touches[0];
            touchStart = beginSwipe(touch.identifier, touch.clientX, touch.clientY);
        }, { passive: true });

        gallery.addEventListener('touchmove', (e) => {
            if (!touchStart) return;

            const touch = Array.from(e.changedTouches).find((item) => item.identifier === touchStart.id)
                || e.touches[0];
            if (!touch) return;

            const delta = updateSwipe(touchStart, touch.clientX, touch.clientY);
            if (touchStart.swiping && delta && Math.abs(delta.dx) > Math.abs(delta.dy)) {
                e.preventDefault();
            }
        }, { passive: false });

        gallery.addEventListener('touchend', (e) => {
            if (!touchStart) return;

            const touch = Array.from(e.changedTouches).find((item) => item.identifier === touchStart.id);
            if (!touch) {
                touchStart = null;
                return;
            }

            const gesture = finishSwipe(touchStart, touch.clientX, touch.clientY);
            touchStart = null;

            if (!gesture.handled) return;

            e.preventDefault();
            e.stopPropagation();
        }, { passive: false });

        gallery.addEventListener('touchcancel', () => {
            touchStart = null;
        }, { passive: true });
    }

    gallery.addEventListener('click', (e) => {
        if (!suppressNextGalleryClick) return;
        e.preventDefault();
        e.stopPropagation();
        suppressNextGalleryClick = false;
    });
};

// Ajaxで後から差し込まれたカード（TOPのおすすめ等）にも張り直せるよう、
// 何度呼んでも同じカードに二重登録しないようにしておく
const main = () => {
    document.querySelectorAll('.c-cards-estate').forEach((card) => {
        bindGalleryNav(card);

        const link = card.querySelector('.c-cards-estate__link');
        if (!link) return;
        if (card.dataset.cardsEstateBound) return;
        card.dataset.cardsEstateBound = '1';

        card.addEventListener('click', (e) => {
            if (e.target.closest(INTERACTIVE_SELECTOR)) return;
            const href = link.getAttribute('href');
            if (!href || href === '#') return;
            // 修飾キーでの新規タブ等はネイティブ挙動に任せるため、通常クリックのみ
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            window.location.href = href;
        });
    });
};

export default main;
